import { Trans, useTranslation } from 'react-i18next';

import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';
import * as monthUtils from '@actual-app/core/shared/months';

import { BotonCerrarHoja } from '#components/common/ModalHoja';
import { Boton } from '#components/mobile/ui/Boton';
import { Hoja } from '#components/mobile/ui/Hoja';
import { Importe } from '#components/mobile/ui/Importe';
import {
  color,
  densidad,
  num,
  radio,
  suave,
} from '#components/mobile/ui/tokens';
import { PrivacyFilter } from '#components/PrivacyFilter';
import { useCategoriesById } from '#hooks/useCategories';
import { useFormat } from '#hooks/useFormat';
import { useLocale } from '#hooks/useLocale';
import { useNavigate } from '#hooks/useNavigate';
import { SheetNameProvider } from '#hooks/useSheetName';
import { useSheetValue } from '#hooks/useSheetValue';
import { useUndo } from '#hooks/useUndo';
import { collapseModals, pushModal } from '#modals/modalsSlice';
import { useDispatch } from '#redux';
import { envelopeBudget } from '#spreadsheet/bindings';

import { filasResumen } from './hojasPlan';
import type { FilaResumen } from './hojasPlan';

type HojaResumenProps = {
  month: string;
  onBudgetAction: (month: string, type: string, args?: unknown) => unknown;
  onCerrar: () => void;
};

/**
 * Resumen del mes (B6 · H43): «¿De dónde salen los {importe}?» con filas
 * tipo recibo (ingresos, sobrante del mes pasado, asignado, reservado…) sin
 * signos «-», el total en una tarjeta verde y «Asignar ahora» / «Reservar
 * para el mes que viene». Si se ha asignado de más, «Cubrir lo asignado de
 * más». Sustituye en el móvil a `envelope-budget-summary`.
 */
export function HojaResumen(props: HojaResumenProps) {
  return (
    <SheetNameProvider name={monthUtils.sheetForMonth(props.month)}>
      <HojaResumenContenido {...props} />
    </SheetNameProvider>
  );
}

type CampoEnvelope =
  | 'total-income'
  | 'from-last-month'
  | 'last-month-overspent'
  | 'total-budgeted'
  | 'buffered-selected'
  | 'buffered'
  | 'to-budget';

function useValor(nombre: CampoEnvelope): number {
  return useSheetValue<'envelope-budget', CampoEnvelope>(nombre) ?? 0;
}

function HojaResumenContenido({
  month,
  onBudgetAction,
  onCerrar,
}: HojaResumenProps) {
  const { t } = useTranslation();
  const format = useFormat();
  const locale = useLocale();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { showUndoNotification } = useUndo();
  const {
    data: { list: categorias } = {
      list: {} as Record<string, { name: string }>,
    },
  } = useCategoriesById();

  const total = useValor(envelopeBudget.toBudget);
  const reservaManual = useValor(envelopeBudget.manualBuffered);
  const filas = filasResumen({
    ingresos: useValor(envelopeBudget.totalIncome),
    delMesPasado: useValor(envelopeBudget.fromLastMonth),
    gastadoDeMas: useValor(envelopeBudget.lastMonthOverspent),
    asignado: useValor(envelopeBudget.totalBudgeted),
    reservado: useValor(envelopeBudget.forNextMonth),
    total,
  });

  const mesCorto = (m: string) => monthUtils.format(m, 'MMMM', locale);
  const etiquetas: Record<FilaResumen['id'], string> = {
    ingresos: t('Income in {{month}}', { month: mesCorto(month) }),
    'mes-pasado': t('Left over from {{month}}', {
      month: mesCorto(monthUtils.prevMonth(month)),
    }),
    'gastado-de-mas': t('Overspent in {{month}}', {
      month: mesCorto(monthUtils.prevMonth(month)),
    }),
    asignado: t('Assigned in {{month}}', { month: mesCorto(month) }),
    reservado: t('Held for {{month}}', {
      month: mesCorto(monthUtils.nextMonth(month)),
    }),
  };

  const importeTotal = format(Math.abs(total), 'financial');
  const titulo =
    total < 0
      ? t('Why are you short {{amount}}?', { amount: importeTotal })
      : t('Where does {{amount}} come from?', { amount: importeTotal });

  const reservar = () => {
    dispatch(
      pushModal({
        modal: {
          name: 'hold-buffer',
          options: {
            month,
            onSubmit: amount => {
              void onBudgetAction(month, 'hold', { amount });
              showUndoNotification({
                message: t('{{amount}} held for {{month}}.', {
                  amount: format(amount, 'financial'),
                  month: mesCorto(monthUtils.nextMonth(month)),
                }),
              });
            },
            onQuitar: () => {
              void onBudgetAction(month, 'reset-hold');
              showUndoNotification({ message: t('Hold removed.') });
            },
          },
        },
      }),
    );
  };

  const cubrir = () => {
    dispatch(
      pushModal({
        modal: {
          name: 'cover',
          options: {
            title: t('Cover overbudgeted'),
            month,
            showToBeBudgeted: false,
            amount: total,
            onSubmit: (amount, categoryId) => {
              void onBudgetAction(month, 'cover-overbudgeted', {
                category: categoryId,
                amount,
                currencyCode: format.currency.code,
              });
              dispatch(collapseModals({ rootModalName: 'cover' }));
              showUndoNotification({
                message: t('Covered overbudgeted from {{categoryName}}', {
                  categoryName: categorias[categoryId]?.name ?? '',
                }),
              });
            },
          },
        },
      }),
    );
  };

  return (
    <Hoja aria-label={titulo} data-testid="hoja-resumen" onCerrar={onCerrar}>
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          gap: 12,
          marginBottom: 10,
        }}
      >
        <Text
          role="heading"
          aria-level={2}
          style={{
            flex: 1,
            fontSize: 20,
            fontWeight: 800,
            letterSpacing: '-0.02em',
            lineHeight: 1.15,
            color: color.fg,
          }}
        >
          {titulo}
        </Text>
        <BotonCerrarHoja onPress={onCerrar} />
      </View>

      <View data-testid="resumen-filas" style={{ marginBottom: 12 }}>
        {filas.map((fila, i) => (
          <View
            key={fila.id}
            data-testid={`resumen-${fila.id}`}
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: 10,
              minHeight: 44,
              borderTop: i > 0 ? `1px dashed ${color.line}` : undefined,
            }}
          >
            <Text
              aria-hidden
              style={{
                width: 16,
                fontWeight: 800,
                color: fila.suma ? color.ok : color.fg3,
                textAlign: 'center',
              }}
            >
              {fila.suma ? '+' : '−'}
            </Text>
            <Text
              style={{
                flex: 1,
                fontSize: 15,
                fontWeight: 600,
                color: color.fg,
              }}
            >
              {etiquetas[fila.id]}
            </Text>
            <Importe
              valor={fila.importe}
              style={{ ...densidad.cifra, fontSize: 15, color: color.fg }}
            />
          </View>
        ))}
      </View>

      <View
        data-testid="resumen-total"
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          padding: '14px 16px',
          borderRadius: radio.tarjeta,
          backgroundColor: total < 0 ? suave(color.bad) : color.okSoft,
          marginBottom: 14,
        }}
      >
        <Text
          style={{
            flex: 1,
            fontSize: 15,
            fontWeight: 800,
            color: total < 0 ? color.bad : color.fg,
          }}
        >
          {total < 0 ? (
            <Trans>Assigned more than you have</Trans>
          ) : (
            <Trans>Ready to assign</Trans>
          )}
        </Text>
        <PrivacyFilter>
          <Text
            style={{
              ...num,
              fontSize: 22,
              fontWeight: 800,
              color: total < 0 ? color.bad : color.ok,
            }}
          >
            {importeTotal}
          </Text>
        </PrivacyFilter>
      </View>

      <View style={{ gap: 8 }}>
        {total < 0 ? (
          <Boton bloque data-testid="resumen-cubrir" onPress={cubrir}>
            <Trans>Cover what you assigned over</Trans>
          </Boton>
        ) : (
          total > 0 && (
            <Boton
              bloque
              data-testid="resumen-asignar"
              onPress={() => void navigate(`/asignar?month=${month}`)}
            >
              <Trans>Assign now</Trans>
            </Boton>
          )
        )}
        {(total > 0 || reservaManual > 0) && (
          <Boton
            bloque
            variante="fantasma"
            data-testid="resumen-reservar"
            onPress={reservar}
          >
            <Trans>Hold for next month</Trans>
          </Boton>
        )}
      </View>
    </Hoja>
  );
}
